import {AppContext, AppContextI} from "../../context/ContextWrapper";
import {ChangeEvent, KeyboardEvent, useContext, useEffect, useState} from "react";
import {Spot} from "../../types";
import {Box, FormControl, FormErrorMessage, Input, Select, Text} from "@chakra-ui/react";
import ManagedCheckBox from "../../form/ManagedCheckBox";
import HelpTooltip from "../../help/HelpTooltip";


type Props = {
    spot: Spot
}

const AdvancedSpotDetails = ({spot}: Props) => {
    const appContext: AppContextI = useContext<AppContextI>(AppContext);
    const isOnDemandMode = appContext.isOnDemand();
    const bleEnabled = appContext.isBleEnabled();
    const [priority, setPriority] = useState<number>(spot.priority || 2);
    const [beaconSensitivity, setBeaconSensitivity] = useState<number>(spot.beaconSensitivity || 8);
    const [canBeStopped, setCanBeStopped] = useState<boolean>(spot?.canBeStopped !== undefined ? spot?.canBeStopped : false);
    const [externalIdentifier, setExternalIdentifier] = useState<string>(spot?.externalIdentifier || "");
    const [identifierError, setIdentifierError] = useState<string | undefined>(undefined);

    useEffect(() => {
        setPriority(spot.priority || 2);
        setBeaconSensitivity(spot.beaconSensitivity || 8);
        setCanBeStopped(spot?.canBeStopped !== undefined ? spot?.canBeStopped : false);
        setExternalIdentifier(spot?.externalIdentifier || "");
        setIdentifierError(undefined);
    }, [spot.id, spot.priority, spot.beaconSensitivity, spot.canBeStopped, spot.externalIdentifier]);

    useEffect(() => {
        if (priority !== spot.priority || canBeStopped !== spot.canBeStopped || beaconSensitivity !== spot.beaconSensitivity) {
            appContext.updateSpot(spot, {
                ...spot,
                priority: priority,
                canBeStopped: canBeStopped,
                beaconSensitivity: beaconSensitivity,
            } as Spot)
        }
    }, [appContext, beaconSensitivity, canBeStopped, priority, spot])

    const validateIdentifier = (value: string) => {
        const trimmed = value.trim().toUpperCase();
        if (trimmed === "") {
            return {normalized: "", error: undefined};
        }
        if (!/^[0-9A-F]+$/.test(trimmed)) {
            return {normalized: trimmed, error: "Use 2-8 hex characters (0-9, A-F)."};
        }
        // Remove insignificant padding zeros on both ends
        const normalized = trimmed.replace(/^0+/, "").replace(/0+$/, "");
        if (normalized.length === 0) {
            return {normalized: "", error: "Identifier cannot be only zeros"};
        }
        if (normalized.length < 2 || normalized.length > 8) {
            return {normalized, error: "Use 2-8 hex characters (0-9, A-F)."};
        }
        const duplicate = appContext.getSpots().some((otherSpot) => {
            const otherNormalized = (otherSpot.externalIdentifier || "")
                .trim()
                .toUpperCase()
                .replace(/^0+/, "")
                .replace(/0+$/, "");
            return otherSpot.id !== spot.id && otherNormalized === normalized;
        });
        if (duplicate) {
            return {normalized, error: "Identifier already used in this space"};
        }
        return {normalized, error: undefined};
    };

    const handleIdentifierBlur = () => {
        const {normalized, error} = validateIdentifier(externalIdentifier);
        setIdentifierError(error);
        if (error) {
            return;
        }
        const currentIdentifier = spot.externalIdentifier ? spot.externalIdentifier.toUpperCase() : "";
        if (normalized === currentIdentifier) {
            setExternalIdentifier(currentIdentifier);
            return;
        }
        appContext.updateSpot(spot, {
            ...spot,
            externalIdentifier: normalized || undefined
        } as Spot);
        setExternalIdentifier(normalized);
    };

    const setEvtPriority = (evt: ChangeEvent<HTMLSelectElement>) => {
        setPriority(+evt.target.value);
    }

    const setEvtBeaconSensitivity = (evt: ChangeEvent<HTMLSelectElement>) => {
        setBeaconSensitivity(+evt.target.value);
    }

    // Note: Spots with higher priority will always stop this spot

    return <>
        {!isOnDemandMode ? (
            <>
                <Box marginBottom={"16px"}>
                    <Text fontWeight={"semibold"}>Priority:</Text>
                    <Select name={"Priority"}
                            value={priority}
                            onChange={setEvtPriority}
                            size={"sm"}
                            disabled={!appContext.isDraft()}
                    >
                        <option value={1}>Low</option>
                        <option value={2}>Normal</option>
                        <option value={4}>High</option>
                        <option value={6}>Highest</option>
                    </Select>
                </Box>
                <ManagedCheckBox name={"canBeStopped"} defaultValue={canBeStopped} onChange={setCanBeStopped}
                                 disabled={!appContext.isDraft()}>
                    <Text>Stop this spot when another spot with the same priority is detected</Text>
                </ManagedCheckBox>
            </>
        ) : null}

        {bleEnabled ? (
            <Box marginTop={"32px"}>
                <HelpTooltip content={"Sets how strong a beacon signal must be before this spot triggers. Higher values are more sensitive and react from farther away; lower values keep the trigger tighter."}>
                    <Text fontWeight={"semibold"}>Beacon sensitivity:</Text>
                </HelpTooltip>
                <Select name={"BeaconSensitivity"}
                        value={beaconSensitivity}
                        onChange={setEvtBeaconSensitivity}
                        size={"sm"}
                        disabled={!appContext.isDraft()}
                >
                    <option value={1}>1 (-50) - least sensitive</option>
                    <option value={2}>2 (-55)</option>
                    <option value={3}>3 (-60)</option>
                    <option value={4}>4 (-65)</option>
                    <option value={5}>5 (-70)</option>
                    <option value={6}>6 (-75)</option>
                    <option value={7}>7 (-80)</option>
                    <option value={8}>8 (-85) - default</option>
                    <option value={9}>9 (-90)</option>
                    <option value={10}>10 (-95) - most sensitive</option>
                </Select>
            </Box>
        ) : null}
        {!isOnDemandMode ? (
            <FormControl marginTop={"24px"} isInvalid={!!identifierError}>
                <HelpTooltip content={"Optional identifier to be used for group synchronization across different spaces"}>
                    <Text fontWeight={"semibold"}>Spot Identifier</Text>
                </HelpTooltip>
                <Input size={"sm"}
                       value={externalIdentifier}
                       placeholder={"2-8 hex characters"}
                       onChange={(event: ChangeEvent<HTMLInputElement>) => {
                           const value = event.target.value;
                           const {error} = validateIdentifier(value);
                           setIdentifierError(error);
                           setExternalIdentifier(value);
                       }}
                       onBlur={handleIdentifierBlur}
                       onKeyDown={(event: KeyboardEvent<HTMLInputElement>) => {
                           if (event.key === "Enter") {
                               event.currentTarget.blur();
                           }
                       }}
                       disabled={!appContext.isDraft()}
                       maxLength={8}
                />
                <FormErrorMessage>{identifierError}</FormErrorMessage>
            </FormControl>
        ) : null}
    </>;
}

export default AdvancedSpotDetails
